import { useEffect, useRef } from "react";
import {
  ArrowUpRightIcon,
  ChevronRightIcon,
} from "../../icons";
import { DropdownCrossIcon } from "../../../components/Icons";
import StatusIcon from "../../../components/Common/StatusIcon";
import { FormatDate } from "../../../helper";

const DetailField = ({ label, value, underline = false, trailingIcon }) => (
  <div className="flex items-start justify-between gap-4 py-3.5 text-sm leading-[21px]">
    <p className="shrink-0 font-normal text-text-secondary lg:text-text-primary">
      {label}
    </p>
    <div className="flex min-w-0 max-w-[188px] items-center justify-end gap-0.5">
      <p
        className={`truncate= text-right font-medium text-text-primary ${
          underline ? "underline decoration-solid underline-offset-2" : ""
        }`}
      >
        {value || "-"}
      </p>
      {trailingIcon}
    </div>
  </div>
);

const SwapDetailFields = ({ transaction }) => {
  const type = transaction.transaction_type?.toLowerCase() || "";
  const isCredit = type === "credit";

  // Flow direction for main overview
  const primarySign = isCredit ? "+" : "-";
  const primaryColorClass = isCredit ? "text-[#4ab932]" : "text-error-500";
  const primaryAmountText = `${primarySign} ${transaction.amount} ${transaction.crypto_currency_code || ""}`;

  const secondarySign = isCredit ? "-" : "+";
  const secondaryText =
    transaction.crypto_amount && transaction.currency_code
      ? `${secondarySign} ${transaction.crypto_amount} ${transaction.currency_code}`
      : null;

  return (
    <div className="flex flex-col gap-6">
      {/* Logos Indicator for Swap */}
      {transaction.logo && (
        <div className="flex items-center justify-center gap-[13px]">
          <div className="flex size-[47px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-background-muted">
            <img
              src={transaction.logo}
              alt={transaction.crypto_currency_code || "crypto logo"}
              className="size-[47px] rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        {/* Amount & Status Overview */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col items-center gap-1.5">
            <p className={`w-full text-center text-[28px] font-semibold leading-6 ${primaryColorClass}`}>
              {primaryAmountText}
            </p>
            {secondaryText && (
              <p className="w-full text-center text-sm font-medium leading-[21px] text-text-secondary">
                {secondaryText}
              </p>
            )}
          </div>

          <div className="flex items-center justify-center gap-1">
            <StatusIcon status={transaction.status} crypto={true} />
            <p className="text-sm font-medium leading-[21px] text-text-primary capitalize">
              {transaction.status}
            </p>
          </div>
        </div>

        {/* Info Card */}
        <div className="rounded-[10px] border divide-[#dbdbdb] bg-background-alt px-4">
          <div className="flex flex-col divide-y border-divider">
            <DetailField label="Date" value={FormatDate(transaction.transaction_date, "d m, yyyy")} />
            <DetailField label="Transaction Nature" value="Swap" />
            {transaction.remarks && (
              <DetailField label="Remarks" value={transaction.remarks} />
            )}
            {transaction.crypto_amount && transaction.currency_code && (
              <DetailField
                label={`${transaction.currency_code} Value`}
                value={`${transaction.crypto_amount} ${transaction.currency_code}`}
              />
            )}
            {transaction.crypto_network && (
              <DetailField label="Asset" value={transaction.crypto_network} />
            )}
            {/* {transaction.to_wallet_address && (
              <DetailField label="Wallet Address" value={`${transaction.to_wallet_address.slice(0, 8)}.....${transaction.to_wallet_address.slice(-8)}`} />
            )} */}
            <DetailField
              label="TXID"
              value={transaction.transaction_id}
              underline
              trailingIcon={
                <ArrowUpRightIcon
                  size={12}
                  className="shrink-0 text-text-primary"
                />
              }
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StandardDetailFields = ({ transaction }) => {
  const type = transaction.transaction_type?.toLowerCase() || "";
  const isCredit = type === "credit";

  // Flow direction for main overview
  const primarySign = isCredit ? "+" : "-";
  const primaryColorClass = isCredit ? "text-[#4ab932]" : "text-error-500";
  const primaryAmountText = `${primarySign} ${transaction.amount} ${transaction.crypto_currency_code || ""}`;

  const secondarySign = isCredit ? "-" : "+";
  const secondaryText = transaction.crypto_amount && transaction.currency_code
      ? `${secondarySign} ${transaction.crypto_amount} ${transaction.currency_code}`
      : null;

  return (
    <div className="flex flex-col gap-6">
      {transaction.logo && (
        <div className="flex items-center justify-center gap-[13px]">
          {/* <div className="flex size-[47px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-background-muted">
            <img
              src={transaction.logo}
              alt={transaction.crypto_currency_code || "crypto logo"}
              className="size-[47px] rounded-full object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div> */}
          <div className="relative shrink-0 size-[47px]">
              <img
                src={transaction.logo}
                alt={transaction.crypto_currency_code || "crypto logo"}
                className="size-12 rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />

              {transaction?.asset && (
                <img
                  src={transaction?.asset?.logo_url}
                  alt={transaction?.asset?.name}
                  className="absolute -bottom-1 -right-0 h-5 w-5 rounded-full border-[1px] border-white bg-white object-contain"
                />
              )}
            </div>
        </div>
      )}

      {/* Amount & Status Overview */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-1.5">
          <p className={`w-full text-center text-3xl font-medium leading-[21px] ${primaryColorClass}`}>
            {primaryAmountText}
          </p>
          {secondaryText && (
            <span className="whitespace-nowrap text-md font-normal leading-[16.5px] text-text-secondary">
              {secondaryText}
            </span>
          )}
        </div>

        <div className="flex items-center justify-center gap-1">
          <StatusIcon status={transaction.status} crypto={true} />
          <p className="text-sm font-medium leading-[21px] text-text-primary capitalize">
            {transaction.status}
          </p>
        </div>
      </div>

      {/* Info Card */}
      <div className="rounded-[10px] border border-[#dbdbdb] bg-background-alt px-4">
        <div className="flex flex-col divide-y border-divider">
          <DetailField label="Date" value={FormatDate(transaction.transaction_date, "d m, yyyy")} />
          {transaction.transaction_nature && (
            <DetailField label="Transaction Nature" value={transaction.transaction_nature} />
          )}
          {transaction.remarks && (
            <DetailField label="Remarks" value={transaction.remarks} />
          )}
          {transaction.crypto_amount && transaction.currency_code && (
            <DetailField
              label={`${transaction.currency_code} Value`}
              value={`${transaction.crypto_amount} ${transaction.currency_code}`}
            />
          )}
          {transaction.crypto_network && (
            <DetailField label="Asset" value={transaction.crypto_network} />
          )}
          {transaction.to_wallet_address && (
            <DetailField label="Wallet Address" value={`${transaction.to_wallet_address.slice(0, 8)}.....${transaction.to_wallet_address.slice(-8)}`} />
          )}
          <DetailField
            label="TXID"
            value={transaction.transaction_id}
            underline
            trailingIcon={
              <ArrowUpRightIcon
                size={12}
                className="shrink-0 text-text-primary"
              />
            }
          />
        </div>
      </div>
    </div>
  );
};

const TransactionDetailsPanel = ({ transaction, onBack, onCancel }) => {
  const panelRef = useRef(null);

  // Trigger smooth scroll-to-top whenever transaction_id changes
  useEffect(() => {
    if (panelRef.current) {
      panelRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [transaction?.transaction_id]);

  // View Transition handling
  const handleBackWithTransition = (e) => {
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        onBack?.(e);
      });
    } else {
      onBack?.(e);
    }
  };

  if (!transaction) {
    return (
      <div className="flex w-full flex-1 flex-col transition-all duration-300 ease-in-out animate-in fade-in slide-in-from-right-4 lg:max-w-[423px] lg:slide-in-from-bottom-2">
        <div className="relative flex items-center justify-between border-b-0 py-0 transition-colors duration-200 lg:py-4">
          <p className="hidden text-[20px] font-semibold leading-[28px] text-text-primary lg:block">
            Details
          </p>
        </div>
        <p className="text-sm text-text-secondary">
          Select a transaction to view details.
        </p>
      </div>
    );
  }

  // Detect Swap via transaction_nature or remarks text
  const rawNature = transaction.transaction_nature?.toLowerCase() || "";
  const remarks = transaction.remarks?.toLowerCase() || "";
  const isSwap = rawNature === "swap" || remarks.includes("swap");

  const displayHeaderTitle =
    transaction.remarks && transaction.remarks.trim().length > 0
      ? transaction.remarks
      : transaction.account_title || `Transaction #${transaction.transaction_id}`;

  return (
    <div
      ref={panelRef}
      className="flex w-full flex-1 flex-col gap-6 transition-all duration-300 ease-in-out animate-in fade-in slide-in-from-right-4 lg:max-w-[423px] lg:slide-in-from-bottom-2"
    >
      {/* Desktop Header */}
      <div className="relative flex items-center justify-between border-b-0 py-0 transition-colors duration-200 lg:py-4">
        <p className="hidden text-[20px] font-semibold leading-[28px] text-text-primary lg:block">
          Details
        </p>

        <DropdownCrossIcon
          onClick={onCancel}
          className="hidden rounded-full p-1 transition-transform duration-200 hover:bg-option-hover active:scale-95 lg:block"
        />
      </div>

      {/* Header Info Bar */}
      <div className="relative flex items-center justify-between border-b-0 py-0 transition-colors duration-200 lg:border-b lg:border-divider lg:py-4">
        {/* Mobile Back Button */}
        <button
          type="button"
          onClick={handleBackWithTransition}
          className="rounded-full p-1 transition-transform duration-200 hover:bg-option-hover active:scale-95 lg:hidden"
          aria-label="Go back"
        >
          <ChevronRightIcon className="rotate-180 text-text-secondary transition-colors duration-200" />
        </button>

        {/* User / Account Details Info */}
        <div className="flex flex-1 flex-col items-center gap-1 transition-all duration-200 lg:flex-row lg:items-center lg:justify-start lg:gap-1">
          <div className="flex items-center gap-2 min-w-0">
            {/* {transaction.logo && (
              <img
                src={transaction.logo}
                alt={transaction.crypto_currency_code || "crypto logo"}
                className="size-5 shrink-0 rounded-full object-cover transition-transform duration-300 hover:scale-105 lg:size-[30px]"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )} */}

            <div className="relative shrink-0">
              <img
                src={transaction.logo}
                alt={transaction.crypto_currency_code || "crypto logo"}
                className="size-9 rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />

              {transaction?.asset && (
                <img
                  src={transaction?.asset?.logo_url}
                  alt={transaction?.asset?.name}
                  className="absolute -bottom-1 -right-0 h-4 w-4 rounded-full border-[1px] border-white bg-white object-contain"
                />
              )}
            </div>
            <p className="hidden truncate text-base text-wrap font-semibold leading-4 text-text-primary lg:block">
              {displayHeaderTitle}
            </p>
            <p className="block truncate text-wrap text-base font-semibold leading-4 text-text-primary lg:hidden">
              {displayHeaderTitle}
            </p>
          </div>
        </div>

        {/* Mobile Top Share Button */}
        {/* <button
          type="button"
          className="rounded-full p-1 transition-transform duration-200 hover:bg-option-hover active:scale-95 lg:hidden"
          aria-label="Share"
        >
          <ShareUploadIcon size={20} className="text-text-primary" />
        </button> */}
      </div>

      {/* Main Details Body */}
      <div className="transition-opacity duration-300 ease-in-out">
        {isSwap ? (
          <SwapDetailFields transaction={transaction} />
        ) : (
          <StandardDetailFields transaction={transaction} />
        )}
      </div>

      {/* Bottom Share Button (Desktop) */}
      {/* <button
        type="button"
        className="hidden h-10 w-full items-center justify-center gap-1 rounded-[8px] bg-[#4ab933] text-sm font-semibold leading-[21px] text-white transition-all duration-200 hover:bg-[#3fa82c] hover:shadow-md active:scale-[0.99] lg:flex"
      >
        <ShareUploadIcon className="text-white" />
        Share
      </button> */}
    </div>
  );
};

export default TransactionDetailsPanel;